/**
 * The HTML the editor is allowed to produce, and the markup it has to produce.
 *
 * Rich-text bodies in `content/` are HTML strings that templates print raw.
 * They were written by Webflow's editor, so they carry Webflow's markup — and
 * the site's stylesheet was built against exactly that. An editor that emits
 * its own idea of an image or a table would render the 73rd Article
 * differently from the other 72, with no error anywhere, which is the failure
 * this module exists to prevent.
 *
 * Two halves, and the panel needs both in both places:
 *
 *   `toEditorHtml`   what the editor loads: Webflow's markup turned into the
 *                    plain elements TipTap understands.
 *   `toContentHtml`  what gets committed: the editor's output turned back into
 *                    Webflow's markup, then sanitised.
 *
 * `sanitize` runs on the server as well as in the browser, because the browser
 * half is a convenience and the server half is the rule. It is a small
 * allow-list over a tokeniser rather than a DOM walk: Cloudflare Workers have
 * no `DOMParser`, and a sanitiser that only runs where there is a DOM is a
 * sanitiser that does not run.
 *
 * ## What is refused, and why each one
 *
 *   `<script>`    arbitrary code on a clinic page. The one exception is
 *                 `type="application/ld+json"`: three Articles carry
 *                 hand-written FAQ structured data that has to reach the page
 *                 exactly as written, and lib/rich-text.js passes script blocks
 *                 through untouched for that reason. Only an Admin may insert
 *                 one, which is enforced by the caller, not here.
 *   `<iframe>`    someone else's page inside ours, and the usual way a tracker
 *                 or a form arrives.
 *   `<style>`     a stylesheet in a body can restyle the whole page.
 *   `on*`         an event handler is a `<script>` with a different spelling.
 *   `style=`      Webflow's own editor emits these and the callout below needs
 *                 one, so it is allowed only on the elements that carry it in
 *                 the existing content; everything pasted from Word loses it.
 *   Webflow hosts a URL that stops resolving the day the subscription ends.
 *                 CLAUDE.md forbids them in the output and the build fails on
 *                 one, so it fails here first, where it can be pointed at.
 *   `https://www.allbrainsclinic.com/…`
 *                 a link from the site to itself, through the internet. After
 *                 cutover it is a link to a stale copy. lib/rich-text.js
 *                 rewrites these at render time; doing it here as well means
 *                 the stored text is right rather than repaired.
 */

import { IMAGE_SCHEMES, LINK_SCHEMES, TAG_RE, decodeEntities, linkTarget, parseAttrs } from "../../lib/content-rules.mjs";

// The tag grammar, the attribute grammar and the judgement of a link's scheme
// come from lib/content-rules.mjs, where tools/check-content.mjs reads the
// same three. That is what makes the build a twin of this sanitiser rather
// than a second opinion: a body this module would refuse fails
// `npm run check:content`, however it got into `content/` (audit S9, S10).

const WEBFLOW_HOST_RE = /website-files\.com|uploads-ssl\.webflow\.com|s3\.amazonaws\.com/i;
const SITE_ORIGIN = "https://www.allbrainsclinic.com";

/** Elements a body may contain. Anything else is unwrapped, keeping its text. */
const ALLOWED = new Set([
  "p", "h2", "h3", "h4", "h5", "h6", "strong", "em", "b", "i", "u", "s", "a", "ul", "ol", "li",
  "blockquote", "br", "hr", "figure", "figcaption", "div", "img", "table", "thead",
  "tbody", "tr", "th", "td", "caption", "code", "pre", "sup", "sub",
]);
// `h5` and `h6` are here because existing Articles use them, not because the
// toolbar offers them: this module decides what may be *stored*, and quietly
// unwrapping a heading somebody wrote would lose the structure of their page.
// `h1` is absent — a body inside a page that already has an `<h1>` must not
// contain a second one, so toContentHtml demotes it to `h2` rather than
// dropping the words.
// `span` is deliberately absent. It carries nothing a reader can see, and it is
// what a paste from Word arrives wrapped in — one per run of text, each with a
// font and a size. Unwrapping keeps the words and drops the costume.

/** Elements dropped whole, contents and all. */
const DROPPED = new Set(["script", "style", "iframe", "object", "embed", "form", "input", "button", "link", "meta"]);

/** Attributes each element may keep. `*` applies to every element. */
const ALLOWED_ATTRS = {
  "*": new Set(["class", "id"]),
  a: new Set(["href", "target", "rel", "title"]),
  img: new Set(["src", "alt", "width", "height", "loading"]),
  figure: new Set(["style", "data-rt-type", "data-rt-align", "data-rt-max-width"]),
  div: new Set(["style", "data-rt-embed-type"]),
  td: new Set(["colspan", "rowspan"]),
  th: new Set(["colspan", "rowspan", "scope"]),
};

const VOID = new Set(["br", "hr", "img"]);

/* ------------------------------------------------------------- tokenising */

/**
 * Split HTML into tags, text and comments. Not a parser: it does not build a
 * tree, because none of the rules below need one and a tree is where a
 * sanitiser usually grows a hole.
 *
 * @param {string} html
 * @returns {{kind: "tag"|"text"|"comment", raw: string, name?: string, close?: boolean, attrs?: string, halfTag?: boolean}[]}
 *   `halfTag` marks a lone `<` that a browser would read as the start of a tag
 */
export function tokenize(html) {
  /** @type {{kind: "tag"|"text"|"comment", raw: string, name?: string, close?: boolean, attrs?: string, halfTag?: boolean}[]} */
  const out = [];
  let i = 0;
  while (i < html.length) {
    const lt = html.indexOf("<", i);
    if (lt === -1) {
      if (i < html.length) out.push({ kind: "text", raw: html.slice(i) });
      break;
    }
    if (lt > i) out.push({ kind: "text", raw: html.slice(i, lt) });

    if (html.startsWith("<!--", lt)) {
      const end = html.indexOf("-->", lt);
      const stop = end === -1 ? html.length : end + 3;
      out.push({ kind: "comment", raw: html.slice(lt, stop) });
      i = stop;
      continue;
    }
    const match = TAG_RE.exec(html.slice(lt));
    if (!match) {
      // A bare `<` in prose. Keep it as text so the sentence survives — but
      // say whether a browser would agree that it is text. `a < b` and `<3`
      // are text to a browser too. `<img src=x onerror=… //` with no `>`, or
      // `<img src=x" onerror=…>` with a stray quote, are not: a browser reads
      // the `<` as the start of a tag and finishes it with the next `>` it
      // finds — the template's own `</div>` — and the handler runs. Emitting
      // that `<` raw stored the attack as text for the browser to assemble
      // (audit S10); `sanitize` writes these as `&lt;` instead.
      out.push({ kind: "text", raw: "<", halfTag: /[A-Za-z/!?]/.test(html[lt + 1] || "") });
      i = lt + 1;
      continue;
    }
    out.push({
      kind: "tag",
      raw: match[0],
      close: match[1] === "/",
      name: match[2].toLowerCase(),
      attrs: match[3] || "",
    });
    i = lt + match[0].length;
  }
  return out;
}

function escapeAttr(value) {
  return String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/* -------------------------------------------------------------- link rules */

/**
 * A link the panel is willing to store.
 *
 * Absolute links back to this site become relative; everything external is
 * left alone but marked `rel="noopener"` when it opens a new tab. What the
 * link may *be* is an allow-list, judged as a browser judges it: `http`,
 * `https`, `mailto`, `tel`, or relative (lib/content-rules.mjs LINK_SCHEMES,
 * `linkTarget`). A deny-list over the raw string — `javascript:`, `data:`,
 * `vbscript:` — was what this used to be, and `java\nscript:alert(1)` and
 * `\u0001javascript:alert(1)` walked past it into a link Chrome ran on click
 * (audit S9): browsers strip tab and newline anywhere, and C0 controls at the
 * ends, before they read a scheme. The URL parser strips the same, so it is
 * the one asked. The caller hands this the value with character references
 * decoded (`decodeEntities`), because a browser decodes `javascript&colon;`
 * before it reads the scheme too.
 *
 * @returns {string|null} null when the link should be unwrapped to plain text
 */
export function cleanHref(href) {
  const link = linkTarget(href);
  if (!link || !LINK_SCHEMES.has(link.protocol)) return null;
  const { value } = link;
  if (WEBFLOW_HOST_RE.test(value)) return null;
  if (value.startsWith(`${SITE_ORIGIN}/`)) return value.slice(SITE_ORIGIN.length);
  if (value === SITE_ORIGIN) return "/";
  return value;
}

/**
 * An image src the panel is willing to store: a local `/images/…` path, or an
 * `http(s)` address. Judged the same way as a link, for the same reason.
 */
export function cleanSrc(src) {
  const link = linkTarget(src);
  if (!link || !IMAGE_SCHEMES.has(link.protocol)) return null;
  const { value } = link;
  if (WEBFLOW_HOST_RE.test(value)) return null;
  if (value.startsWith(`${SITE_ORIGIN}/`)) return value.slice(SITE_ORIGIN.length);
  return value;
}

/* ------------------------------------------------------------- sanitising */

/**
 * @typedef {object} SanitizeResult
 * @property {string} html    what may be stored
 * @property {string[]} notes what was changed, in words, for the editor to show
 */

/**
 * Reduce a body to the allow-list above.
 *
 * Disallowed elements are **unwrapped** rather than deleted (their text
 * survives), except for the few in `DROPPED`, whose contents are not text a
 * reader was ever meant to see. That choice matters for a paste from Word: a
 * `<span style="font-family:Calibri">` should leave the sentence behind, not
 * take it away.
 *
 * @param {string} html
 * @param {{allowJsonLd?: boolean}} [options] JSON-LD is an Admin-only door
 * @returns {SanitizeResult}
 */
export function sanitize(html, { allowJsonLd = false } = {}) {
  if (typeof html !== "string" || !html.trim()) return { html: "", notes: [] };

  const notes = new Set();
  const out = [];
  const tokens = tokenize(html);
  /** How deep we are inside something being dropped whole. */
  let dropping = 0;
  let droppingName = "";
  /**
   * The elements actually written out, innermost last.
   *
   * Without this, unwrapping an opening `<span>` or a `javascript:` link left
   * its `</span>` or `</a>` behind — a closing tag with nothing to close, which
   * browsers recover from by guessing and which would slowly turn a body into
   * markup nobody wrote.
   */
  const open = [];
  /**
   * Inside an allowed JSON-LD block. Its contents are the author's JSON, passed
   * through untouched up to and including the `</script>` that ends it. The
   * old code wrote the opening tag and then let the closing one fall into the
   * DROPPED branch, which swallowed it — so the block never closed and the
   * browser read the rest of the page, footer and all, as script (audit S11).
   */
  let inJsonLd = false;

  for (const token of tokens) {
    if (inJsonLd) {
      if (token.kind === "tag" && token.name === "script" && token.close) {
        out.push("</script>");
        inJsonLd = false;
      } else {
        out.push(token.raw);
      }
      continue;
    }

    if (token.kind === "comment") continue;

    if (dropping > 0) {
      if (token.kind === "tag" && token.name === droppingName) {
        if (token.close) dropping--;
        else dropping++;
      }
      continue;
    }

    if (token.kind === "text") {
      if (token.halfTag) {
        // A `<` a browser would read as the start of a tag, with no tag behind
        // it that this could read. Stored raw, the browser finishes it with the
        // template's next `>` and whatever attributes it carried take effect.
        // As `&lt;` it is the text it claimed to be.
        out.push("&lt;");
        notes.add("An unfinished tag was written out as text rather than left for the browser to finish.");
        continue;
      }
      out.push(token.raw);
      continue;
    }

    const name = token.name;

    if (DROPPED.has(name)) {
      const attrs = parseAttrs(token.attrs || "");
      const isJsonLd =
        name === "script" && attrs.some(([k, v]) => k === "type" && v.toLowerCase() === "application/ld+json");
      if (isJsonLd && allowJsonLd && !token.close) {
        // The one shape the three Articles carry, rather than the tag as
        // typed: nothing else on a script element is wanted, and a body that
        // has been through this once comes back byte for byte.
        out.push('<script type="application/ld+json">');
        inJsonLd = true;
        continue;
      }
      if (!token.close) {
        dropping = 1;
        droppingName = name;
        notes.add(
          isJsonLd
            ? "A structured-data block was removed. Only an Admin can add one."
            : `A <${name}> was removed — the site does not publish them from an article body.`
        );
      }
      continue;
    }

    if (!ALLOWED.has(name)) {
      // Unwrap: the tag goes, its text stays. Its closing tag falls into the
      // same branch and is dropped with it.
      if (!token.close) notes.add(`<${name}> is not used on this site; its text was kept.`);
      continue;
    }

    if (token.close) {
      // Only close something that is actually open, and close anything left
      // open inside it on the way — an orphan `</p>` is dropped rather than
      // emitted.
      const at = open.lastIndexOf(name);
      if (at === -1) continue;
      while (open.length > at) out.push(`</${open.pop()}>`);
      continue;
    }

    const kept = [];
    const allowed = ALLOWED_ATTRS[name] || new Set();
    const global = ALLOWED_ATTRS["*"];
    for (const [key, raw] of parseAttrs(token.attrs || "")) {
      // Judged and stored as a browser reads it: `&colon;` is `:` to Chrome
      // before it looks at the scheme, and `&amp;` in a query string is `&`.
      // Before this, the raw string was judged — `javascript&colon;` passed as
      // a relative link — and then escaped again, so every save turned `&amp;`
      // into `&amp;amp;` and broke the link. escapeAttr below encodes once.
      const value = decodeEntities(raw);
      if (key.startsWith("on")) {
        notes.add("An event handler was removed from a tag.");
        continue;
      }
      if (!allowed.has(key) && !global.has(key)) {
        if (key === "style") notes.add("Pasted styling was removed; the site's stylesheet decides how text looks.");
        continue;
      }
      if (key === "href") {
        const href = cleanHref(value);
        if (href === null) {
          notes.add(`A link to ${value.slice(0, 60)} was removed.`);
          continue;
        }
        if (href !== value) notes.add("A link to this site was made relative.");
        kept.push(["href", href]);
        continue;
      }
      if (key === "src") {
        const src = cleanSrc(value);
        if (src === null) {
          notes.add("An image hosted on Webflow was removed — upload it to the media library instead.");
          continue;
        }
        kept.push(["src", src]);
        continue;
      }
      // `id=""` is invalid HTML and Webflow emitted ~2,000 of them. It is kept
      // here anyway: lib/rich-text.js already drops it on the way to the page,
      // and removing it on save would rewrite bodies nobody edited.
      kept.push([key, value]);
    }

    if (name === "a" && !kept.some(([k]) => k === "href")) continue;
    if (name === "img" && !kept.some(([k]) => k === "src")) continue;
    if (name === "a" && kept.some(([k, v]) => k === "target" && v === "_blank") && !kept.some(([k]) => k === "rel")) {
      kept.push(["rel", "noopener"]);
    }

    const attrs = kept.map(([k, v]) => ` ${k}="${escapeAttr(v)}"`).join("");
    out.push(`<${name}${attrs}>`);
    if (!VOID.has(name) && !/\/\s*>$/.test(token.raw)) open.push(name);
  }

  // Anything still open at the end of a body — a `<p>` whose close tag the
  // editor never wrote — is closed here rather than shipped unbalanced. A
  // JSON-LD block with no `</script>` would otherwise swallow the page after it.
  if (inJsonLd) out.push("</script>");
  while (open.length) out.push(`</${open.pop()}>`);

  let result = out.join("");
  if (WEBFLOW_HOST_RE.test(result)) {
    result = result.replace(new RegExp(`https?://[^\\s"'<>]*(?:${WEBFLOW_HOST_RE.source})[^\\s"'<>]*`, "gi"), "");
    notes.add("A Webflow address was removed from the text.");
  }
  if (result.includes(`${SITE_ORIGIN}/`)) {
    result = result.split(`${SITE_ORIGIN}/`).join("/");
    notes.add("Addresses on this site were made relative.");
  }

  return { html: result, notes: [...notes] };
}

/* ----------------------------------------------------- Webflow's own shapes */

/** An image in a body, as Webflow wraps one. `align` is "center" or "fullwidth". */
export function figureHtml({ src, alt = "", align = "center", maxWidth = "1024px" }) {
  return (
    `<figure class="w-richtext-figure-type-image w-richtext-align-${align}" style="max-width:${maxWidth}"` +
    ` data-rt-type="image" data-rt-align="${align}" data-rt-max-width="${maxWidth}">` +
    `<div><img alt="${escapeAttr(alt)}" src="${escapeAttr(src)}" width="auto" height="auto" loading="auto"></div>` +
    `</figure>`
  );
}

/** The "Key Takeaways" callout, exactly as the existing Articles carry it. */
export function calloutHtml({ summary = "", points = [] }) {
  const list = points
    .filter((p) => String(p).trim())
    .map((p) => `        <li>${p}</li>`)
    .join("\n\n");
  return (
    `<div data-rt-embed-type='true'><div class="article-summary" style="background: rgb(41 61 146 / 20%);` +
    `border: 1px solid #d7d9e8;border-radius: 10px;text-align: left;padding: 30px;margin-bottom: 30px;">\n` +
    `    <p><strong>Key Takeaways</strong></p>\n\n` +
    `    <p class="tldr">\n        ${summary}\n    </p>\n\n` +
    `    <ul class="key-takeaways">\n${list}\n    </ul>\n</div></div>`
  );
}

/**
 * Editor HTML -> what is stored.
 *
 * TipTap emits a plain `<img>` for an image and a plain `<table>`; both need
 * Webflow's wrapper to look like the rest of the site. The alignment is carried
 * on the image as `data-align`, which the editor's own node sets.
 */
export function toContentHtml(editorHtml, { allowJsonLd = false } = {}) {
  let html = String(editorHtml || "");

  // The page's own <h1> is its title. A second one in the body competes with it
  // in search results, so it becomes the <h2> it almost always meant.
  html = html.replace(/<(\/?)h1\b/gi, "<$1h2");

  // A bare <img> that is not already inside a figure becomes one.
  html = html.replace(/<img\b([^>]*)>/gi, (whole, attrs) => {
    const map = Object.fromEntries(parseAttrs(attrs));
    if (map["data-in-figure"] === "true") return whole;
    return figureHtml({
      src: map.src || "",
      alt: map.alt || "",
      align: map["data-align"] === "fullwidth" ? "fullwidth" : "center",
    });
  });

  const { html: clean, notes } = sanitize(html, { allowJsonLd });
  return { html: clean, notes };
}

/**
 * What is stored -> what the editor loads.
 *
 * The figure wrapper is unwrapped to the `<img>` TipTap understands, keeping
 * the alignment on the image so that saving puts the wrapper back identically.
 * A body that has been through this pair and back is byte-identical, which
 * tools/../admin/test/markup.test.mjs asserts against real Article bodies.
 */
export function toEditorHtml(contentHtml) {
  let html = String(contentHtml || "");
  html = html.replace(
    /<figure class="w-richtext-figure-type-image w-richtext-align-(center|fullwidth)"[^>]*>\s*<div>\s*(<img\b[^>]*>)\s*<\/div>\s*<\/figure>/gi,
    (_whole, align, img) => {
      const map = Object.fromEntries(parseAttrs(img.replace(/^<img\b/i, "").replace(/>$/, "")));
      return `<img src="${escapeAttr(map.src || "")}" alt="${escapeAttr(map.alt || "")}" data-align="${align}">`;
    }
  );
  return html;
}

/** Words a reader sees, for the editor's word count and reading time. */
export function readingStats(html) {
  const text = String(html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&[a-z]+;|&#\d+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = text ? text.split(" ").length : 0;
  // 225 words a minute, the figure most reading-time estimates use, rounded up
  // so a one-word article does not say "0 min".
  return { words, minutes: Math.max(1, Math.round(words / 225)) };
}
